import type { RefObject } from "react";
import { Banner, Typography } from "@foodpilot/foods";
import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import type { ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import { getEntityFormStickyTop } from "src/components/EntityForm/EntityFormPage";
import type { SectionErrors } from "../validation";
import { getRecipeWeightValidation } from "../recipeWeightValidation";

type ProductValidationBannerProps = {
    bannerRef: RefObject<HTMLDivElement | null>;
    product: ProductFormType;
    errorsBySection: SectionErrors;
};

export const ProductValidationBanner = (props: ProductValidationBannerProps) => {
    const { bannerRef, product, errorsBySection } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const errors = Object.values(errorsBySection).flat();
    const hasWeightMismatch = errors.some((error) => error.field === "recipeWeight");
    const hasRequiredFields = errors.some((error) => error.field !== "recipeWeight");
    const count = Number(hasWeightMismatch) + Number(hasRequiredFields);
    const { totalWeight } = getRecipeWeightValidation(product);

    if (count === 0) return null;

    return (
        <Box
            ref={bannerRef}
            role="alert"
            tabIndex={-1}
            aria-labelledby="product-validation-title"
            sx={{
                scrollMarginTop: getEntityFormStickyTop(theme.spacing(2)),
                borderRadius: theme.spacing(1.5),
                "&:focus": {
                    outline: "none",
                },
                "& > .MuiBox-root": {
                    backgroundColor: theme.custom.red[50],
                    borderColor: theme.custom.red[600],
                    borderWidth: theme.custom.strokes.containerDivider.width,
                    borderStyle: theme.custom.strokes.containerDivider.style,
                    borderRadius: theme.spacing(1.5),
                },
                "& > .MuiBox-root > .MuiStack-root": { alignItems: "flex-start" },
            }}
        >
            <Banner variant="error">
                <Typography
                    component="span"
                    variant="body-semibold"
                    id="product-validation-title"
                    sx={{ display: "block", color: theme.palette.text.primary, marginBottom: theme.spacing(0.5) }}
                >
                    {count === 1 ?
                        t("product.v2.validation.issue", "1 issue needs to be fixed before saving")
                    :   t("product.v2.validation.issues", "{{count}} issues need to be fixed before saving", { count })}
                </Typography>
                <Typography
                    component="span"
                    variant="small-regular"
                    sx={{ display: "block", color: theme.palette.text.secondary }}
                >
                    {t("product.v2.validation.introduction", "To save this product, please correct the following:")}
                </Typography>
                <Box component="span" sx={{ display: "block", marginTop: theme.spacing(1.5) }}>
                    {hasWeightMismatch && (
                        <Typography
                            component="span"
                            variant="small-regular"
                            sx={{ display: "block", color: theme.palette.text.secondary }}
                        >
                            {t(
                                "product.v2.validation.weight_mismatch",
                                "• Total weight after processing ({{total}} g) doesn't match the recipe target ({{net}} g).",
                                {
                                    total: Number(totalWeight.toFixed(2)),
                                    net: product.netWeight,
                                },
                            )}
                        </Typography>
                    )}
                    {hasRequiredFields && (
                        <Typography
                            component="span"
                            variant="small-regular"
                            sx={{ display: "block", color: theme.palette.text.secondary }}
                        >
                            {t(
                                "product.v2.validation.required_fields",
                                "• Every field marked with * must be complete.",
                            )}
                        </Typography>
                    )}
                </Box>
            </Banner>
        </Box>
    );
};
